// Joana chat shell — mini-app bundle trust anchor. The WaveTalk store signs
// every published bundle's exact bytes at publish time with its Ed25519 key
// (`Sema.Api.Miniapps.BundleSigner`); the host refuses to run a bundle whose
// signature doesn't verify against the public half baked in here — fail
// closed, the same posture as the mobile loader (`MiniAppTrust.kt` /
// `MiniAppBundleLoader`). Authenticity therefore doesn't depend on the
// delivery channel: whatever rewrites the bytes can rewrite the hash header
// too, but can't forge the signature.
//
// This is the DEV/TEST key — the public half of the well-known seed in
// `services/api/config/runtime.exs`. A production deploy must ship the
// production key here (print it with
// `Sema.Api.Miniapps.BundleSigner.public_key_hex/0`).

/** The store's bundle-signing public key (Ed25519, raw 32 bytes, hex). */
export const MINIAPP_PUBLISHER_PUBLIC_KEY_HEX =
  "19036b7189a5ea3f296e342a387a57ba93a16ebefa653c22d5a8fe66ba772799";

/**
 * The key in effect: a deployment may pin its own publisher key via the
 * `window.__MINIAPP_PUBLISHER_KEY__` global (set by the host page, like
 * `__API_BASE__`), so the same bundle serves dev and prod. Defaults to the
 * baked-in dev key. (A page-level attacker able to set globals can already
 * run arbitrary code in the trusted shell — this adds no new surface.)
 */
export function currentPublisherKeyHex(): string {
  const g = globalThis as { __MINIAPP_PUBLISHER_KEY__?: unknown };
  return typeof g.__MINIAPP_PUBLISHER_KEY__ === "string" && g.__MINIAPP_PUBLISHER_KEY__.length === 64
    ? g.__MINIAPP_PUBLISHER_KEY__
    : MINIAPP_PUBLISHER_PUBLIC_KEY_HEX;
}

/** Raised when a bundle is unsigned, malformed, or fails verification. */
export class BundleSignatureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BundleSignatureError";
  }
}

/**
 * Verify the store's detached Ed25519 signature over the exact bundle bytes
 * via WebCrypto. Resolves on success; throws [BundleSignatureError] on a
 * missing/malformed/invalid signature or a runtime without Ed25519 support —
 * every failure path refuses the bundle (fail closed).
 */
export async function verifyBundleSignature(
  bytes: Uint8Array,
  signatureHex: string | null | undefined,
  publisherKeyHex: string = MINIAPP_PUBLISHER_PUBLIC_KEY_HEX,
): Promise<void> {
  const sigHex = (signatureHex ?? "").trim().toLowerCase();
  if (sigHex.length === 0) {
    throw new BundleSignatureError("bundle is unsigned (no x-sema-package-signature); refusing to run it");
  }

  let signature: Uint8Array;
  let publicKey: Uint8Array;
  try {
    signature = hexToBytes(sigHex);
    publicKey = hexToBytes(publisherKeyHex);
  } catch {
    throw new BundleSignatureError("bundle signature (or publisher key) is not valid hex");
  }

  let valid = false;
  try {
    const key = await crypto.subtle.importKey(
      "raw",
      publicKey as unknown as BufferSource,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
    valid = await crypto.subtle.verify(
      "Ed25519",
      key,
      signature as unknown as BufferSource,
      bytes as unknown as BufferSource,
    );
  } catch (e) {
    // No Ed25519 in this runtime (or a WebCrypto refusal) is still a refusal
    // to run unverified code — never a bypass.
    throw new BundleSignatureError(`bundle signature could not be verified: ${(e as Error).message}`);
  }
  if (!valid) {
    throw new BundleSignatureError("bundle signature does not verify against the publisher key");
  }
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) {
    throw new Error("not hex");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(2 * i, 2 * i + 2), 16);
  }
  return out;
}
